<script setup lang="ts">
import { ref, computed, watch, nextTick } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import KekkleBottomSheet from "@/components/ui/KekkleBottomSheet.vue";
import KekkleEmptyState from "@/components/ui/KekkleEmptyState.vue";
import KekkleFilterTabs, {
  type FilterTabOption,
} from "@/components/ui/KekkleFilterTabs.vue";
import NotificationsHeader from "@/components/notifications/NotificationsHeader.vue";
import NotificationsList from "@/components/notifications/NotificationsList.vue";
import KekkleProgressBar from "@/components/ui/KekkleProgressBar.vue";
import { useInAppNotificationsStore } from "@/stores/modules/notifications/in-app-notifications";
import type { NotificationDocument } from "@kekkle/shared/types/documents/notification-document/notification-document";
import {
  CollectionIcon,
  SparklesIcon,
  CheckCircleIcon,
} from "@heroicons/vue/outline";

type Props = {
  open: boolean;
};

type Emits = {
  (e: "update:open", value: boolean): void;
};

type TabId = "all" | "unread" | "read";

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const { t } = useI18n();
const router = useRouter();
const notificationsStore = useInAppNotificationsStore();

const activeTab = ref<TabId>("unread");
const skipHistoryCleanup = ref<boolean>(false);
const isStale = computed(() => notificationsStore.isStale);

watch(
  () => props.open,
  (newOpen) => {
    if (newOpen) {
      activeTab.value = "unread";
    }
  },
);

const isOpen = computed<boolean>({
  get: () => props.open,
  set: (val: boolean) => emit("update:open", val),
});

const unreadNotifications = computed(() =>
  notificationsStore.notifications.filter((n) => !n.is_read),
);

const readNotifications = computed(() =>
  notificationsStore.notifications.filter((n) => n.is_read),
);

const filteredNotifications = computed(() => {
  switch (activeTab.value) {
    case "unread":
      return unreadNotifications.value;
    case "read":
      return readNotifications.value;
    case "all":
    default:
      return notificationsStore.notifications;
  }
});

const tabOptions = computed<FilterTabOption<TabId>[]>(() => [
  {
    id: "all",
    label: t("notifications.tabs.all", "All"),
    count: notificationsStore.notifications.length,
    icon: CollectionIcon,
    variant: "purple",
  },
  {
    id: "unread",
    label: t("notifications.tabs.unread", "Unread"),
    count: notificationsStore.unreadCount,
    icon: SparklesIcon,
    variant: "yellow",
  },
  {
    id: "read",
    label: t("notifications.tabs.read", "Read"),
    count: readNotifications.value.length,
    icon: CheckCircleIcon,
    variant: "blue",
  },
]);

const emptyTitle = computed(() => {
  switch (activeTab.value) {
    case "unread":
      return t("notifications.empty_unread_title");
    case "read":
      return t("notifications.empty_read_title");
    case "all":
    default:
      return t("notifications.empty_title");
  }
});

const emptyDescription = computed(() => {
  switch (activeTab.value) {
    case "unread":
      return t("notifications.empty_unread_description");
    case "read":
      return t("notifications.empty_read_description");
    case "all":
    default:
      return t("notifications.empty_description");
  }
});

async function handleNotificationClick(
  notification: NotificationDocument,
): Promise<void> {
  if (!notification.is_read) {
    notificationsStore.markAsRead(notification._id);
  }
  skipHistoryCleanup.value = true;
  emit("update:open", false);

  if (notification.link) {
    try {
      await router.push(notification.link);
    } catch (err) {
      console.error("Navigation error:", err);
    }
  }

  await nextTick();
  skipHistoryCleanup.value = false;
}
</script>

<template>
  <KekkleBottomSheet
    v-model:open="isOpen"
    :skip-history-cleanup="skipHistoryCleanup"
    shadow="yellow"
    padding="normal"
    initial-height="full"
  >
    <div class="flex h-full flex-col dark:text-slate-200">
      <!-- Header -->
      <NotificationsHeader
        :unread-count="notificationsStore.unreadCount"
        @mark-all-read="notificationsStore.markAllAsRead"
      />

      <!-- Stale / Loading Progress Bar -->
      <KekkleProgressBar
        :active="isStale || notificationsStore.isLoading"
        color="yellow"
        class="-mt-2 mb-3"
      />

      <!-- Filter Tabs -->
      <div class="mb-3">
        <KekkleFilterTabs v-model="activeTab" :options="tabOptions" />
      </div>

      <!-- Notifications List -->
      <NotificationsList
        v-if="filteredNotifications.length > 0"
        :notifications="filteredNotifications"
        @select="handleNotificationClick"
      />

      <!-- Empty State -->
      <div v-else class="my-auto py-12">
        <KekkleEmptyState
          emoji="🔔"
          :title="emptyTitle"
          :description="emptyDescription"
        />
      </div>
    </div>
  </KekkleBottomSheet>
</template>
